import { Controller } from '@hotwired/stimulus'
import { isNumber, find, round } from 'lodash'

import { closeFlashAlert, flashAlert } from '../../shared/flash'
export default class extends Controller {

  static targets = [
    'applyTip',
    'container',
    'customAmount',
    'customContainer',
    'customTip',
    'option',
  ]

  connect() {
    this.fetchHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    }
    this.orderId = this.containerTarget.dataset.orderId;

    this.checkoutContainer = document.querySelector('#checkout-container')
    // ! NOTE: once only or we'll create a loop when cart fetches order on tip change.
    this.checkoutContainer.addEventListener('orderLoaded', this.onOrderLoaded, { once: true })
  }

  onOrderLoaded = ({ detail }) => {
    this.order = detail.order;
    this.tipAmountCents = this.order.tip_amount_cents || 0;

    const selectedTip = this.selectedTipPercentage();

    if (selectedTip) {
      this.removeSelections();
      selectedTip.classList.add('selected');
    } else {
      this.selectCustomTip();
    }

    this.dispatchTipUpdate();
  }

  selectCustomTip() {
    // If the tip amount is 0 (or negative), do not select the custom tip option
    if (this.tipAmountCents <= 0) {
      return;
    }

    this.initiateCustomTip();
    this.customAmountTarget.value = (this.tipAmountCents / 100).toFixed(2);
  }

  selectedTipPercentage() {
    const discountedTotalCents = this.order.discounted_item_total_cents || 0;
    let selectedTarget;

    if (this.order.tip_percentage > 0) {
      selectedTarget = this.tipPercentage(this.order.tip_percentage);
    } else if (this.order.tip_amount_cents > 0 && discountedTotalCents > 0) {
      const tolerance = 0.01; // Tolerance for matching implied percentage
      const impliedPercentage = (this.tipAmountCents / discountedTotalCents) * 100;
      const roundedImpliedPercentage = round(impliedPercentage, 2);
      selectedTarget = this.tipPercentage(roundedImpliedPercentage, tolerance);
    }

    return selectedTarget;
  }

  tipPercentage(tipPercentage, tolerance = 0) {
    return find(this.optionTargets, target => {
      const percentage = parseInt(target.dataset.percentage);
      const diff = Math.abs(percentage - tipPercentage);
      return round(diff, 2) <= tolerance;
    });
  }

  onTipPercentageChange = event => {
    this.customContainerTarget.classList.add('hidden');
    this.removeSelections();

    const selectedOption = event.target;
    selectedOption.classList.add('selected');

    const percentage = parseInt(selectedOption?.dataset?.percentage || 0);

    if (!this.order || !isNumber(this.order.discounted_item_total_cents)) {
      flashAlert('Could not calculate tip. Order data is missing.');
      return;
    }

    const tipAmountCents = Math.round((this.order.discounted_item_total_cents * percentage) / 100);

    this.updateTip({ amount: tipAmountCents });
  }

  initiateCustomTip = _event => {
    this.customContainerTarget.classList.remove('hidden');
    this.applyTipTarget.disabled = true
    this.customAmountTarget.focus();
    this.customAmountTarget.value = '';
    this.removeSelections();
    this.customTipTarget.classList.add('selected');
  }

  applyCustomTip(event) {
    event.preventDefault();
    const amount = parseFloat(this.customAmountTarget.value || 0) * 100;
    this.updateTip({ amount });
  }

  customChange() {
    if (parseFloat(this.customAmountTarget.value) > 0) {
      this.applyTipTarget.removeAttribute('disabled');
    } else {
      this.applyTipTarget.setAttribute('disabled', true);
    }
  }

  removeSelections() {
    this.optionTargets.forEach(target => {
      target.classList.remove('selected')
    })
    this.customTipTarget.classList.remove('selected')
  }

  dispatchTipUpdate() {
    const tipUpdateEvent = new CustomEvent('tipUpdate');
    this.checkoutContainer.dispatchEvent(tipUpdateEvent);
  }

  updateTip = params => {
    closeFlashAlert();
    fetch(
      `/sandbar/admin/orders/${this.orderId}/add_tip`,
      {
        method: 'PUT',
        body: JSON.stringify(params),
        headers: this.fetchHeaders,
      }
    )
      .then(() => {
        const addToCartEvent = new CustomEvent('orderCartAdd', { detail: { orderId: this.orderId } });
        this.checkoutContainer.dispatchEvent(addToCartEvent);
        this.dispatchTipUpdate()
      })
      .catch(err => flashAlert(err?.message));
  }
}
